import React from 'react';
import { CheckCircle, CheckSquareIcon, ClipboardList, List, ListChecks, Scale, TestTube } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export function userTypeDetails(type: string): {
	label: string;
	variant: BadgeProps['variant'];
	className?: string;
	icon: React.ReactNode;
} {
	switch (type) {
		case 'live':
			return { label: 'Live', variant: 'success', icon: <CheckCircle className="inline mr-2 h-4 w-4" /> };
		case 'sandbox':
			return { label: 'Sandbox', variant: 'neutral', icon: <TestTube className="inline mr-2 h-4 w-4" /> };
		case 'discussion':
			return { label: 'Discussion', variant: 'default', icon: <ListChecks className="inline mr-2 h-4 w-4" /> };
		case 'eval':
			// No badge variant for the chart palette, so background and hover are set together.
			return {
				label: 'Evaluation',
				variant: 'default',
				className: 'bg-chart-3 hover:bg-chart-3/80',
				icon: <CheckSquareIcon className="inline mr-2 h-4 w-4" />,
			};
		case 'eval_run':
			return {
				label: 'Eval Run',
				variant: 'default',
				className: 'bg-chart-3 hover:bg-chart-3/80',
				icon: <ClipboardList className="inline mr-2 h-4 w-4" />,
			};
		case 'rated':
			// The rating behind a rated user may be positive or negative, so the icon stays silent about which.
			return { label: 'Rated', variant: 'warning', icon: <Scale className="inline mr-2 h-4 w-4" /> };
		default:
			return { label: type, variant: 'neutral', icon: <List className="inline mr-2 h-4 w-4" /> };
	}
}

/** The user type as the grid shows it; `size="xs"` fits a popover header. */
export function UserTypeBadge({ type, size }: { type: string; size?: 'xs' }) {
	const { label, variant, className, icon } = userTypeDetails(type);
	return (
		<Badge
			variant={variant}
			className={cn('whitespace-nowrap', size === 'xs' && 'h-5 px-1.5 py-0 text-xs [&_svg]:mr-1 [&_svg]:h-3 [&_svg]:w-3', className)}
		>
			{icon}
			{label}
		</Badge>
	);
}
